import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Trash2, Edit3, Upload, Star, Eye, EyeOff, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, clearToken, getToken } from "../lib/api";
import { useI18n } from "../lib/i18n";
import useTitle from "../lib/useTitle";
import { TYPE_META, TYPE_KEYS } from "../lib/constants";

const emptyAcc = {
  name: "", type: "hostel", description_en: "", description_fr: "",
  location_name: "", google_maps_url: "", latitude: "", longitude: "",
  images: [], hostelworld_url: "", whatsapp_url: "",
  is_featured: false, is_published: true,
};
const emptyGuide = {
  title_en: "", title_fr: "", description_en: "", description_fr: "",
  cover_image: "", pdf_url: "", category: "", is_published: true,
};

const TabBtn = ({ active, onClick, children, ...props }) => (
  <button onClick={onClick} {...props}
    className={`px-5 py-2.5 rounded-full text-sm font-medium transition ${active ? "bg-wostel-ink text-sand-50" : "bg-transparent text-wostel-ink/70 hover:bg-sand-200"}`}>
    {children}
  </button>
);

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wider text-wostel-ink/60 mb-1.5 block">{label}</span>
    <input {...props} className="w-full bg-sand-50 border border-sand-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-wostel-cyan" />
  </label>
);

const TextArea = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wider text-wostel-ink/60 mb-1.5 block">{label}</span>
    <textarea {...props} className="w-full bg-sand-50 border border-sand-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-wostel-cyan" />
  </label>
);

function StayForm({ value, onChange, onSubmit, onCancel, t }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ ...value, images: [...(value.images || []), r.data.url] });
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImage = (i) => onChange({ ...value, images: value.images.filter((_, idx) => idx !== i) });

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-5" data-testid="stay-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t.admin.name + "*"} required value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} data-testid="stay-input-name"/>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-wostel-ink/60 mb-1.5 block">{t.admin.type}*</span>
          <select required value={value.type} onChange={(e) => onChange({ ...value, type: e.target.value })} data-testid="stay-input-type"
            className="w-full bg-sand-50 border border-sand-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-wostel-cyan">
            {TYPE_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <TextArea label={t.admin.description_en} rows={4} value={value.description_en || ""} onChange={(e) => onChange({ ...value, description_en: e.target.value })} data-testid="stay-input-desc-en"/>
        <TextArea label={t.admin.description_fr} rows={4} value={value.description_fr || ""} onChange={(e) => onChange({ ...value, description_fr: e.target.value })} data-testid="stay-input-desc-fr"/>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t.admin.location_name} value={value.location_name || ""} onChange={(e) => onChange({ ...value, location_name: e.target.value })} data-testid="stay-input-location"/>
        <Field label={t.admin.google_maps_url} value={value.google_maps_url || ""} onChange={(e) => onChange({ ...value, google_maps_url: e.target.value })} data-testid="stay-input-maps"/>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t.admin.latitude} type="number" step="any" value={value.latitude ?? ""} onChange={(e) => onChange({ ...value, latitude: e.target.value === "" ? null : Number(e.target.value) })} data-testid="stay-input-lat"/>
        <Field label={t.admin.longitude} type="number" step="any" value={value.longitude ?? ""} onChange={(e) => onChange({ ...value, longitude: e.target.value === "" ? null : Number(e.target.value) })} data-testid="stay-input-lng"/>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t.admin.hostelworld_url} value={value.hostelworld_url || ""} onChange={(e) => onChange({ ...value, hostelworld_url: e.target.value })} data-testid="stay-input-hw"/>
        <Field label={t.admin.whatsapp_url} value={value.whatsapp_url || ""} onChange={(e) => onChange({ ...value, whatsapp_url: e.target.value })} data-testid="stay-input-wa"/>
      </div>

      <div>
        <span className="text-xs uppercase tracking-wider text-wostel-ink/60 mb-2 block">{t.admin.images}</span>
        <div className="flex gap-3 flex-wrap mb-3">
          {(value.images || []).map((url, i) => (
            <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-sand-300">
              <img src={url} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5" data-testid={`stay-img-remove-${i}`}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" id="stay-image-upload" data-testid="stay-image-file"/>
        <label htmlFor="stay-image-upload" className="inline-flex items-center gap-2 cursor-pointer btn-pill bg-sand-200 text-wostel-ink hover:bg-sand-300 text-sm" data-testid="stay-upload-btn">
          <Upload size={14}/> {uploading ? "..." : t.admin.uploadImage}
        </label>
      </div>

      <div className="flex gap-6 flex-wrap pt-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={value.is_featured} onChange={(e) => onChange({ ...value, is_featured: e.target.checked })} data-testid="stay-input-featured"/>
          {t.admin.featured}
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={value.is_published} onChange={(e) => onChange({ ...value, is_published: e.target.checked })} data-testid="stay-input-published"/>
          {t.admin.published}
        </label>
      </div>

      <div className="flex gap-3 pt-3 border-t border-sand-300">
        <button type="submit" className="btn-pill bg-wostel-ink text-sand-50 hover:bg-wostel-cyan" data-testid="stay-submit">{t.admin.save}</button>
        <button type="button" onClick={onCancel} className="btn-pill bg-transparent border border-sand-300 hover:bg-sand-200" data-testid="stay-cancel">{t.admin.cancel}</button>
      </div>
    </form>
  );
}

function GuideForm({ value, onChange, onSubmit, onCancel, t }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onUploadPdf = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/upload/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ ...value, pdf_url: r.data.url });
      toast.success("PDF uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-5" data-testid="guide-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t.admin.title_en + "*"} required value={value.title_en} onChange={(e) => onChange({ ...value, title_en: e.target.value })} data-testid="guide-input-title-en"/>
        <Field label={t.admin.title_fr} value={value.title_fr || ""} onChange={(e) => onChange({ ...value, title_fr: e.target.value })} data-testid="guide-input-title-fr"/>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <TextArea label={t.admin.description_en} rows={3} value={value.description_en || ""} onChange={(e) => onChange({ ...value, description_en: e.target.value })} data-testid="guide-input-desc-en"/>
        <TextArea label={t.admin.description_fr} rows={3} value={value.description_fr || ""} onChange={(e) => onChange({ ...value, description_fr: e.target.value })} data-testid="guide-input-desc-fr"/>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label={t.admin.category} value={value.category || ""} onChange={(e) => onChange({ ...value, category: e.target.value })} data-testid="guide-input-cat"/>
        <Field label={t.admin.cover_image} value={value.cover_image || ""} onChange={(e) => onChange({ ...value, cover_image: e.target.value })} data-testid="guide-input-cover"/>
      </div>
      <div>
        <span className="text-xs uppercase tracking-wider text-wostel-ink/60 mb-1.5 block">{t.admin.pdf_url}*</span>
        <div className="flex gap-3 items-center flex-wrap">
          <input required value={value.pdf_url} onChange={(e) => onChange({ ...value, pdf_url: e.target.value })} placeholder="https://..."
            className="flex-1 min-w-[200px] bg-sand-50 border border-sand-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-wostel-cyan" data-testid="guide-input-pdf"/>
          <input ref={fileRef} type="file" accept="application/pdf" onChange={onUploadPdf} className="hidden" id="guide-pdf-upload" data-testid="guide-pdf-file"/>
          <label htmlFor="guide-pdf-upload" className="inline-flex items-center gap-2 cursor-pointer btn-pill bg-sand-200 text-wostel-ink hover:bg-sand-300 text-sm" data-testid="guide-upload-btn">
            <Upload size={14}/> {uploading ? "..." : t.admin.uploadPdf}
          </label>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={value.is_published} onChange={(e) => onChange({ ...value, is_published: e.target.checked })} data-testid="guide-input-published"/>
        {t.admin.published}
      </label>
      <div className="flex gap-3 pt-3 border-t border-sand-300">
        <button type="submit" className="btn-pill bg-wostel-ink text-sand-50 hover:bg-wostel-cyan" data-testid="guide-submit">{t.admin.save}</button>
        <button type="button" onClick={onCancel} className="btn-pill bg-transparent border border-sand-300 hover:bg-sand-200" data-testid="guide-cancel">{t.admin.cancel}</button>
      </div>
    </form>
  );
}

export default function AdminDashboard() {
  const { t } = useI18n();
  useTitle(t.admin.dashboard);
  const nav = useNavigate();
  const [tab, setTab] = useState("stays");
  const [stays, setStays] = useState([]);
  const [guides, setGuides] = useState([]);
  const [creators, setCreators] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null); // { kind:'stay'|'guide', data, isNew }

  useEffect(() => {
    if (!getToken()) { nav("/admin/login"); return; }
    api.get("/auth/me").catch(() => { clearToken(); nav("/admin/login"); });
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    try {
      const [s, g, c, m] = await Promise.all([
        api.get("/admin/accommodations"),
        api.get("/admin/guides"),
        api.get("/admin/creator-applications"),
        api.get("/admin/contact-submissions"),
      ]);
      setStays(s.data || []); setGuides(g.data || []);
      setCreators(c.data || []); setContacts(m.data || []);
    } catch (err) {
      if (err?.response?.status === 401) { clearToken(); nav("/admin/login"); }
    }
  };

  const logout = () => { clearToken(); nav("/admin/login"); };

  const saveStay = async () => {
    const d = editing.data;
    const payload = { ...d, latitude: d.latitude === "" ? null : d.latitude, longitude: d.longitude === "" ? null : d.longitude };
    try {
      if (editing.isNew) await api.post("/admin/accommodations", payload);
      else await api.put(`/admin/accommodations/${d.id}`, payload);
      toast.success("Saved"); setEditing(null); refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const saveGuide = async () => {
    try {
      if (editing.isNew) await api.post("/admin/guides", editing.data);
      else { /* guides update not implemented; just delete+create */ }
      toast.success("Saved"); setEditing(null); refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const deleteStay = async (id) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    await api.delete(`/admin/accommodations/${id}`); toast.success("Deleted"); refresh();
  };
  const deleteGuide = async (id) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    await api.delete(`/admin/guides/${id}`); toast.success("Deleted"); refresh();
  };

  return (
    <div className="min-h-screen bg-sand-100" data-testid="admin-dashboard">
      <Toaster position="top-center" richColors />
      <header className="bg-white border-b border-sand-300/70 sticky top-0 z-30">
        <div className="container-x flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={TYPE_META.hostel.logo} alt="" className="h-8 w-8" />
            <span className="font-serif text-lg">Wostel · {t.admin.dashboard}</span>
          </div>
          <button onClick={logout} className="btn-pill bg-transparent border border-sand-300 text-sm hover:bg-sand-200" data-testid="admin-logout">
            <LogOut size={14}/> {t.admin.logout}
          </button>
        </div>
      </header>

      <div className="container-x py-10">
        <div className="flex gap-2 flex-wrap mb-8" data-testid="admin-tabs">
          <TabBtn active={tab === "stays"} onClick={() => { setTab("stays"); setEditing(null); }} data-testid="tab-stays">{t.admin.tabs.stays} · {stays.length}</TabBtn>
          <TabBtn active={tab === "guides"} onClick={() => { setTab("guides"); setEditing(null); }} data-testid="tab-guides">{t.admin.tabs.guides} · {guides.length}</TabBtn>
          <TabBtn active={tab === "creators"} onClick={() => { setTab("creators"); setEditing(null); }} data-testid="tab-creators">{t.admin.tabs.creators} · {creators.length}</TabBtn>
          <TabBtn active={tab === "contacts"} onClick={() => { setTab("contacts"); setEditing(null); }} data-testid="tab-contacts">{t.admin.tabs.contacts} · {contacts.length}</TabBtn>
        </div>

        {editing && editing.kind === "stay" && (
          <div className="bg-white rounded-3xl border border-sand-300/70 p-8 mb-8">
            <h2 className="font-serif text-2xl mb-6">{editing.isNew ? t.admin.addStay : t.admin.edit}</h2>
            <StayForm value={editing.data} onChange={(d) => setEditing({ ...editing, data: d })} onSubmit={saveStay} onCancel={() => setEditing(null)} t={t} />
          </div>
        )}
        {editing && editing.kind === "guide" && (
          <div className="bg-white rounded-3xl border border-sand-300/70 p-8 mb-8">
            <h2 className="font-serif text-2xl mb-6">{t.admin.addGuide}</h2>
            <GuideForm value={editing.data} onChange={(d) => setEditing({ ...editing, data: d })} onSubmit={saveGuide} onCancel={() => setEditing(null)} t={t} />
          </div>
        )}

        {tab === "stays" && !editing && (
          <div>
            <button onClick={() => setEditing({ kind: "stay", data: { ...emptyAcc }, isNew: true })} className="btn-pill bg-wostel-ink text-sand-50 mb-6 hover:bg-wostel-cyan" data-testid="add-stay-btn">
              <Plus size={14}/> {t.admin.addStay}
            </button>
            <div className="grid gap-3">
              {stays.map((s) => (
                <div key={s.id} className="bg-white rounded-2xl border border-sand-300/70 p-5 flex items-center gap-5 flex-wrap" data-testid={`admin-stay-${s.id}`}>
                  <img src={TYPE_META[s.type]?.logo} alt="" className="h-10 w-10" />
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-wostel-ink/60">{s.type} · {s.location_name}</div>
                  </div>
                  {s.is_featured && <Star size={14} className="text-wostel-sun fill-wostel-sun"/>}
                  {s.is_published ? <Eye size={14} className="text-wostel-ink/40"/> : <EyeOff size={14} className="text-wostel-ink/40"/>}
                  <button onClick={() => setEditing({ kind: "stay", data: s, isNew: false })} className="p-2 rounded-full hover:bg-sand-200" data-testid={`edit-stay-${s.id}`}><Edit3 size={14}/></button>
                  <button onClick={() => deleteStay(s.id)} className="p-2 rounded-full hover:bg-red-50 text-red-500" data-testid={`delete-stay-${s.id}`}><Trash2 size={14}/></button>
                </div>
              ))}
              {stays.length === 0 && <div className="text-wostel-ink/40 py-8 text-sm">No stays yet.</div>}
            </div>
          </div>
        )}

        {tab === "guides" && !editing && (
          <div>
            <button onClick={() => setEditing({ kind: "guide", data: { ...emptyGuide }, isNew: true })} className="btn-pill bg-wostel-ink text-sand-50 mb-6 hover:bg-wostel-cyan" data-testid="add-guide-btn">
              <Plus size={14}/> {t.admin.addGuide}
            </button>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {guides.map((g) => (
                <div key={g.id} className="bg-white rounded-2xl border border-sand-300/70 p-5" data-testid={`admin-guide-${g.id}`}>
                  <div className="font-medium mb-1">{g.title_en}</div>
                  <div className="text-xs text-wostel-ink/60 mb-4">{g.category}</div>
                  <div className="flex gap-2">
                    <a href={g.pdf_url} target="_blank" rel="noreferrer" className="text-xs underline" data-testid={`open-guide-${g.id}`}>Open</a>
                    <button onClick={() => deleteGuide(g.id)} className="ml-auto text-red-500 hover:underline text-xs" data-testid={`delete-guide-${g.id}`}>Delete</button>
                  </div>
                </div>
              ))}
              {guides.length === 0 && <div className="text-wostel-ink/40 py-8 text-sm">No guides yet.</div>}
            </div>
          </div>
        )}

        {tab === "creators" && (
          <div className="space-y-3">
            {creators.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl border border-sand-300/70 p-5" data-testid={`creator-app-${c.id}`}>
                <div className="flex justify-between flex-wrap gap-2">
                  <div className="font-medium">{c.full_name} · <span className="text-wostel-ink/60 font-normal">{c.email}</span></div>
                  <span className="text-xs text-wostel-ink/50">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <div className="text-xs text-wostel-ink/60 mt-1">IG {c.instagram} · TT {c.tiktok} · YT {c.youtube} · {c.audience_size}</div>
                <p className="mt-3 text-sm text-wostel-ink/80 whitespace-pre-line">{c.message}</p>
              </div>
            ))}
            {creators.length === 0 && <div className="text-wostel-ink/40 py-8 text-sm">No applications yet.</div>}
          </div>
        )}

        {tab === "contacts" && (
          <div className="space-y-3">
            {contacts.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl border border-sand-300/70 p-5" data-testid={`contact-msg-${c.id}`}>
                <div className="flex justify-between flex-wrap gap-2">
                  <div className="font-medium">{c.full_name} · <span className="text-wostel-ink/60 font-normal">{c.email}</span></div>
                  <span className="text-xs text-wostel-ink/50">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                {c.subject && <div className="text-xs text-wostel-ink/60 mt-1">Subject: {c.subject}</div>}
                <p className="mt-3 text-sm text-wostel-ink/80 whitespace-pre-line">{c.message}</p>
              </div>
            ))}
            {contacts.length === 0 && <div className="text-wostel-ink/40 py-8 text-sm">No messages yet.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
